import React from 'react';
import { Accordion } from 'react-bootstrap';

import CodeBlock from './CodeBlock';
import { TEST_IDS } from './testIds';
import {
  AUTH_HEADER,
  ENDPOINTS,
  ERRORS,
  MAX_PAGE_SIZE,
  RATE_LIMIT,
  SCOPES,
  SYNC_EXAMPLE,
} from './apiDocs';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const ApiDocsPanel = () => {
  const curlExample =
    `curl -H "Authorization: Api-Key YOUR_KEY" \\\n` +
    `  "${BASE_URL}/api/v1/public/demos/?status=ENDED&limit=50"`;

  return (
    <div data-testid={TEST_IDS.DOCS_PANEL}>
      <div className="h6">Authentication</div>
      <p className="text-muted">
        Send your key in the <code>Authorization</code> header on every request.
        Responses only ever contain your own store&apos;s data.
      </p>
      <CodeBlock label="Request header" value={AUTH_HEADER} />
      <CodeBlock label="Example request" value={curlExample} />

      <div className="h6 mt-4">Endpoints</div>
      <p className="text-muted">
        Base URL <code>{BASE_URL}/api/v1/public/</code>. List endpoints
        paginate with <code>?limit=</code> and <code>?offset=</code>, up to{' '}
        {MAX_PAGE_SIZE} per page. Expand an endpoint for an example request and
        response.
      </p>

      <Accordion className="api-key-docs-accordion">
        {ENDPOINTS.map((endpoint, index) => (
          <Accordion.Item eventKey={String(index)} key={endpoint.id}>
            <Accordion.Header>
              <span className="api-key-docs-method">{endpoint.method}</span>
              <code className="api-key-docs-path">{endpoint.path}</code>
            </Accordion.Header>
            <Accordion.Body>
              <p className="mb-1">{endpoint.summary}</p>
              <p className="text-muted small">{endpoint.detail}</p>
              <CodeBlock label="Request" value={endpoint.request} />
              <div className="mt-2">
                <CodeBlock
                  label="Response"
                  value={endpoint.response}
                  testId={TEST_IDS.docsResponse(endpoint.id)}
                />
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      <div className="h6 mt-4">Keeping data in sync</div>
      <p className="text-muted">
        Rather than re-reading everything, ask for what changed since your last
        run. <code>last_activity_at</code> tracks the newest change across a
        demo <em>and</em> its insights, so a demo whose sentiment analysis
        finished hours after the meeting still shows up. Results come back
        oldest change first, so an interrupted sync can resume where it stopped.
      </p>
      <CodeBlock
        label="Incremental sync"
        value={SYNC_EXAMPLE}
        testId={TEST_IDS.SYNC_EXAMPLE}
      />

      <div className="row mt-4">
        <div className="col-md-6">
          <div className="h6">Scopes</div>
          <p className="text-muted small">
            Keys carry all scopes unless you narrow them.
          </p>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th className="product-head">Scope</th>
                  <th className="product-head">Grants</th>
                </tr>
              </thead>
              <tbody>
                {SCOPES.map(([scope, grants]) => (
                  <tr key={scope}>
                    <td>
                      <code>{scope}</code>
                    </td>
                    <td className="small">{grants}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-6">
          <div className="h6">Responses</div>
          <p className="text-muted small">Rate limit: {RATE_LIMIT}.</p>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th className="product-head">Status</th>
                  <th className="product-head">Meaning</th>
                </tr>
              </thead>
              <tbody>
                {ERRORS.map(([code, meaning]) => (
                  <tr key={code}>
                    <td>
                      <code>{code}</code>
                    </td>
                    <td className="small">{meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="text-muted small mb-0">
        Full reference and a machine-readable OpenAPI schema:{' '}
        <code>{BASE_URL}/api/v1/public/docs/</code>
      </p>
    </div>
  );
};

export default ApiDocsPanel;
